<template>
  <div class="card select-card">
    <p class="eyebrow">Workshop</p>
    <h1>{{ isAdmin ? 'Select a college to view' : 'Select your college' }}</h1>
    <p class="muted lead">
      <template v-if="isAdmin">
        Pick a college to view its poll results. This does not assign you to that college.
      </template>
      <template v-else>
        Choose your college from the list. After facilitator approval, you can access the slides at any time.
      </template>
    </p>

    <template v-if="!requesting">
      <label class="field-label" for="college-search">College</label>
      <div class="suggest" ref="wrap">
        <input
          id="college-search"
          ref="inputEl"
          v-model="query"
          type="text"
          class="input"
          autocomplete="off"
          placeholder="Start typing a college name…"
          :disabled="submitting"
          @focus="open = true"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="confirmHighlighted"
          @keydown.esc="open = false"
        />
        <ul v-if="open && filtered.length" class="menu" role="listbox">
          <li
            v-for="(c, i) in filtered"
            :key="c.id || c.name"
            role="option"
            class="option"
            :class="{ active: i === highlight }"
            @mousedown.prevent="pick(c)"
          >
            <span class="name">{{ isAdmin ? collegeLabel(c, sessions) : c.name }}</span>
          </li>
        </ul>
        <p v-else-if="open && query.trim() && !loading" class="empty muted">
          No matching colleges.
        </p>
      </div>

      <div v-if="selected" class="picked">
        <div>
          <strong>{{ selected.name }}</strong>
        </div>
        <button type="button" class="btn btn-secondary btn-sm" @click="clear">Change</button>
      </div>

      <button
        class="btn btn-primary continue"
        :disabled="!canContinue || submitting"
        @click="confirm"
      >
        {{ submitting ? 'Saving…' : 'Continue' }}
      </button>
      <button v-if="!isAdmin" type="button" class="linkish" @click="requesting = true">
        Can’t find your college?
      </button>
    </template>

    <template v-else>
      <label class="field-label" for="college-request">College name</label>
      <input
        id="college-request"
        v-model="requestName"
        type="text"
        class="input"
        placeholder="Enter your college name"
        :disabled="submitting"
      />
      <p class="muted hint">We’ll reach out after you submit.</p>
      <button
        class="btn btn-primary continue"
        :disabled="!requestName.trim() || submitting"
        @click="submitRequest"
      >
        {{ submitting ? 'Sending…' : 'Submit college name' }}
      </button>
      <button type="button" class="linkish" @click="requesting = false">
        Back to college list
      </button>
    </template>

    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { collegeLabel } from '../services/workshopAccess'
import { getAdminSession, setAdminSession } from '../services/adminSession'
import { listColleges, setMyCollege, requestCollege, getMe, adminListColleges } from '../services/api'

const router = useRouter()
const query = ref('')
const colleges = ref([])
const filtered = ref([])
const selected = ref(null)
const open = ref(false)
const highlight = ref(0)
const loading = ref(false)
const submitting = ref(false)
const requesting = ref(false)
const requestName = ref('')
const isAdmin = ref(false)
const sessions = ref([])
const error = ref('')

const canContinue = computed(() => !!selected.value && (!isAdmin.value || !!selected.value.id))

const wrap = ref(null)
const inputEl = ref(null)
let debounce = null
let searchGen = 0

async function search(q) {
  const gen = ++searchGen
  loading.value = true
  error.value = ''
  try {
    const res = isAdmin.value
      ? { colleges: sessions.value.filter(c => c.name.toLowerCase().includes(q.trim().toLowerCase())) }
      : await listColleges({ q })
    if (gen !== searchGen) return
    colleges.value = res.colleges || []
    filtered.value = colleges.value
    highlight.value = 0
  } catch (e) {
    if (gen !== searchGen) return
    error.value = e.message || 'Could not load colleges'
    filtered.value = []
  } finally {
    if (gen === searchGen) loading.value = false
  }
}

watch(query, (q) => {
  if (selected.value && q !== selected.value.name) {
    selected.value = null
  }
  open.value = true
  clearTimeout(debounce)
  debounce = setTimeout(() => search(q), 180)
})

function pick(c) {
  selected.value = c
  query.value = c.name
  open.value = false
}

function clear() {
  selected.value = null
  query.value = ''
  open.value = true
  inputEl.value?.focus()
  search('')
}

function move(delta) {
  if (!filtered.value.length) return
  open.value = true
  highlight.value = (highlight.value + delta + filtered.value.length) % filtered.value.length
}

function confirmHighlighted() {
  if (!open.value || !filtered.value.length) {
    if (selected.value) confirm()
    return
  }
  pick(filtered.value[highlight.value])
}

async function confirm() {
  if (!canContinue.value || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    if (isAdmin.value) {
      setAdminSession({
        collegeName: selected.value.name,
        collegeId: selected.value.id,
      })
      router.replace('/admin')
      return
    }
    await setMyCollege({ collegeName: selected.value.name })
    router.replace('/workshop-status')
  } catch (e) {
    error.value = e.message || 'Could not save college'
  } finally {
    submitting.value = false
  }
}

async function submitRequest() {
  const name = requestName.value.trim()
  if (!name || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    await requestCollege(name)
    try {
      sessionStorage.setItem('u80_request_thanks', '1')
    } catch {
      /* ignore */
    }
    const { signOut } = await import('../services/auth')
    try {
      await signOut()
    } catch {
      /* already signed out */
    }
    router.replace('/login')
  } catch (e) {
    error.value = e.message || 'Could not send college name'
  } finally {
    submitting.value = false
  }
}

function onDocClick(e) {
  if (wrap.value && !wrap.value.contains(e.target)) {
    open.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', onDocClick)
  try {
    const me = await getMe()
    isAdmin.value = !!me.isAdmin
    if (me.isAdmin) {
      const res = await adminListColleges()
      sessions.value = res.colleges || []
      const viewing = getAdminSession()
      selected.value = sessions.value.find(c => c.id === viewing.collegeId) ||
        sessions.value.find(c => c.name.toLowerCase() === viewing.collegeName?.toLowerCase()) || null
    } else if (me.collegeName) {
      selected.value = { name: me.collegeName }
    }
    if (selected.value) query.value = selected.value.name
    await search('')
  } catch (e) {
    error.value = e.message || 'Could not load colleges'
  }
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  clearTimeout(debounce)
})
</script>

<style scoped>
.select-card {
  max-width: 28rem;
  margin: 0 auto;
  text-align: left;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--teal);
  margin: 0 0 0.5rem;
}

h1 {
  margin: 0 0 0.75rem;
  font-size: 1.75rem;
}

.lead {
  margin: 0 0 1.5rem;
}

.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.suggest {
  position: relative;
}

.input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font: inherit;
  background: var(--cream);
}

.input:focus {
  outline: 2px solid var(--teal-bright);
  outline-offset: 1px;
}

.menu {
  position: absolute;
  z-index: 10;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
  background: var(--cream);
  border: 1px solid var(--border);
  border-radius: 6px;
  box-shadow: var(--shadow-soft);
  max-height: 14rem;
  overflow: auto;
}

.option {
  padding: 0.55rem 0.85rem;
  cursor: pointer;
}

.option:hover,
.option.active {
  background: var(--paper-warm);
}

.option .name {
  font-weight: 600;
}

.empty {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
}

.picked {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.85rem;
  padding: 0.75rem 0.85rem;
  background: var(--paper-warm);
  border-radius: 6px;
  border: 1px solid var(--border);
}



.continue {
  width: 100%;
  margin-top: 1.25rem;
}

.hint {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
}

.linkish {
  display: block;
  margin-top: 0.85rem;
  background: none;
  border: 0;
  padding: 0;
  color: var(--teal);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.btn-sm {
  padding: 0.3rem 0.65rem;
  font-size: 0.85rem;
}

.error {
  color: var(--danger);
  margin-top: 0.85rem;
}
</style>
